"use client";

import { cacheExchange as graphCacheExchange } from "@urql/exchange-graphcache";
import {
	type Exchange,
	SSRContext,
	type SSRData,
	type SSRExchange,
	UrqlProvider,
	cacheExchange,
	createClient,
	fetchExchange,
	mapExchange,
	ssrExchange,
} from "@urql/next";
import {
	type PropsWithChildren,
	createContext,
	use,
	useContext,
	useDeferredValue,
	useMemo,
	useState,
} from "react";

import { Warning } from "@/components/icons/warning";
import { queue } from "@/components/toasts";
import { graphql } from "@whitecircle/graphql";
import schema, {
	Color,
	type Interval,
	type Member,
	type Mutation,
	type Stats,
	type Update,
} from "@whitecircle/graphql/graphql";
import { cnBase } from "tailwind-variants";

export const ssr = () =>
	ssrExchange({
		isClient: typeof window !== "undefined",
	});

export const cache = () =>
	graphCacheExchange({
		schema,
		keys: {
			Stats: () => null,
			Interval: () => null,
			ChatMessage: () => null,
			ChatData: () => null,
			Update: (update) => `${update.id}_${update.createdAt}`,
		},
		resolvers: {
			Member: {
				invited: (parent: Member) => new Date(parent.invited),
			},
			Update: {
				createdAt: (parent: Update) => new Date(parent.createdAt),
			},
		},
		optimistic: {
			feedback(args: { violation: boolean; check: string }) {
				return {
					checkId: args.check,
					violation: args.violation,
					id: args.check,
					__typename: "Feedback",
				};
			},
		},
		updates: {
			Mutation: {
				policy(_mutation: Mutation, _variables, cache) {
					cache.invalidate("Query", "updates");
					cache.invalidate("Query", "policy");
					cache.invalidate("Query", "policies");
				},
				member(_mutation: Mutation, _variables, cache) {
					cache.invalidate("Query", "team");
				},
				deployment(_mutation: Mutation, _variables, cache) {
					cache.invalidate("Query", "deployment");
					cache.invalidate("Query", "deployments");
				},
				review(mutation: Mutation, variables: { session: string }, cache) {
					if (mutation.review && variables.session) {
						cache.link(
							{ __typename: "Session", _id: variables.session },
							"review",
							{
								__typename: "Review",
								id: mutation.review.id,
							},
						);
					}
				},
				feedback(mutation: Mutation, _, cache) {
					if (mutation.feedback?.checkId) {
						cache.link(
							{ __typename: "Check", id: mutation.feedback.checkId },
							"feedback",
							{
								__typename: "Feedback",
								id: mutation.feedback.id,
								violation: mutation.feedback.violation,
							},
						);
					}
				},
			},
		},
	});

export const client = (ssr: SSRExchange, cache: Exchange) =>
	createClient({
		url: `${process.env.ORIGIN || ""}/graphql`,
		exchanges: [
			...(typeof window === "undefined" ? [] : [cache]),
			ssr,
			...(typeof window === "undefined" ? [cacheExchange] : []),
			mapExchange({
				onError(error) {
					console.error(error);
					if (error.networkError) {
						queue.add(
							{
								color: Color.Pink,
								icon: <Warning className="w-5" />,
								message:
									"Unable to process your request. Please try again later.",
							},
							{ timeout: 2000 },
						);
					}
				},
			}),
			fetchExchange,
		],
		fetchOptions: {
			credentials: "include",
			mode: "cors",
		},
		fetchSubscriptions: true,
		suspense: true,
		requestPolicy: typeof window === "undefined" ? "cache-only" : undefined,
	});

export function deserialize(ssr: SSRExchange, data: SSRData) {
	const result = {} as SSRData;
	for (const key in data) {
		result[key] = { ...data[key] };
		if (typeof result[key].error === "string") {
			result[key].error = JSON.parse(result[key].error as unknown as string);
		}
	}
	ssr.restoreData(result);
}

export function Preload(
	props: PropsWithChildren<{
		data:
			| Promise<{ ssr: SSRData } | { ssr: SSRData }[]>
			| { ssr: SSRData }
			| { ssr: SSRData }[];
	}>,
) {
	const data = "then" in props.data ? use(props.data) : props.data;
	const ssr = useContext(SSRContext);

	useMemo(() => {
		if (data && ssr) {
			if (Array.isArray(data)) {
				const ssrData = data.reduce(
					(ssr, data) => Object.assign(ssr, data.ssr),
					{} as SSRData,
				);
				deserialize(ssr, ssrData);
			} else {
				deserialize(ssr, data.ssr);
			}
		}
	}, [data]);

	return props.children;
}

export const ResetContext = createContext(() => {});

export const useReset = () => useContext(ResetContext);

export function Urql({ children }: PropsWithChildren) {
	const [index, setIndex] = useState(0);
	const [clientInstance, ssrInstance] = useMemo(() => {
		const ssrInstance = ssr();
		const cacheInstance = cache();
		const clientInstance = client(ssrInstance, cacheInstance);
		return [clientInstance, ssrInstance];
	}, [index]);
	const reset = () => setIndex((index) => index + 1);

	return (
		<ResetContext.Provider value={reset} key={index}>
			<UrqlProvider client={clientInstance} ssr={ssrInstance}>
				{children}
			</UrqlProvider>
		</ResetContext.Provider>
	);
}
